import "./infographic-summarize-form.scss";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import { useEffect, useState } from "react";
import { selectSummarizeColourStyles } from "../../config/form-select.config";
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import LoadingComponent from "../../components/loading/loading.component";
import { toneOptions } from "../../config/infographic-option.config";
import MediaService from "../../services/media.service";
import { getUniqueSpeakerList } from "../../utility/function.utility";
import CircularProgress from "@mui/material/CircularProgress";
import InfographicService from "../../services/infographic.service";
import { useSnackbar } from "../../utility/snackbar.utility";
import CustomSnackbar from "../../components/snackbar/snackbar.component";

interface NameOption {
    value: string;
    label: string;
}

const InfographicSummarizeFormModal = ({ show, onClose, mediaId, infographic }) => {

    const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

    const [selectTone, setSelectTone] = useState<NameOption>();
    const [selectedSpeakers, setSelectedSpeakers] = useState<string[]>([]);
    const [isDisabled, setIsDisabled] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isShowLoading, setIsShowLoading] = useState<boolean>(false);
    const [speakerList, setSpeakerList] = useState<string[]>([]);

    useEffect(() => {
        initializeForm();
    }, []);

    useEffect(() => {
        setIsDisabled(!selectTone);
    }, [selectTone]);

    const initializeForm = async () => {
        try {
            setIsLoading(true);
            const response = await MediaService.getMediaById(mediaId);
            const uniqueSpeakers = getUniqueSpeakerList(response.data.transcripts, false);
            setSpeakerList(uniqueSpeakers);

            setSelectTone({ value: infographic.template_config.tone, label: infographic.template_config.tone });
            const defaultSpeakers = infographic.template_config.focus_speaker.map(item => `Speaker ${item}`);
            setSelectedSpeakers(defaultSpeakers);
        } catch (error) {
            showSnackbar('', "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        onClose(false);
    }

    const handleToneChange = (event) => {
        setSelectTone(event);
    };

    const handleSpeakerChange = (event: React.SyntheticEvent<Element, Event>, checked: boolean) => {
        const target = event.target as HTMLInputElement;
        const name = target.name;

        setSelectedSpeakers((prev) =>
            checked ? [...prev, name] : prev.filter((speaker) => speaker !== name)
        );
    };

    const onSubmit = async () => {
        if (!selectTone) return;

        setIsShowLoading(true);
        try {
            const focusSpeakers = selectedSpeakers.map(item => Number(item.split(' ')[1]));
            const tempConfig = {
                ...infographic.template_config,
                tone: selectTone?.label || '',
                focus_speaker: focusSpeakers,
            };
            const updateResponse = await InfographicService.updateInfo(
                mediaId,
                infographic.id,
                tempConfig,
                infographic?.raw_images || [],
                infographic?.imageFull || '',
                infographic?.imageThumbnail || '',
                infographic?.text_config || []
            );

            const infographicResponse = await InfographicService.getInfographic(mediaId, infographic.id);
            onClose({
                template_config: updateResponse.data.template_config,
                infoContent: infographicResponse.data.infographic,
                infoQuote: infographicResponse.data.infographic_quote,
                infoTitle: infographicResponse.data.infographic_title,
            });
        } catch (error) {
            showSnackbar('', "error");
        } finally {
            setIsShowLoading(false);
        }
    }

    return (
        <>
            <Modal show={show} onHide={handleClose} centered>
                <Modal.Body className="summarize-modal-wrapper">
                    {
                        isShowLoading ?
                            <div className="mb-3">
                                <LoadingComponent message={'ระบบกำลังทำการ generate เนื้อหา และ infographic<br/> อาจใช้เวลาสักครู่กรุณาอย่าปิดหน้าต่างนี้'} />
                            </div> :
                            isLoading ?
                                <div className="d-flex justify-content-center align-items-center">
                                    <CircularProgress />
                                </div> :
                                <div className="w-100">
                                    <FontAwesomeIcon icon={faClose} className="modal-icon-close" onClick={handleClose}></FontAwesomeIcon>

                                    <p className="summarize-title">
                                        ใส่ข้อมูลเพื่อสรุปเนื้อหาใหม่อีกครั้ง
                                    </p>

                                    <p className="summarize-form-title">
                                        1. เลือก Tone ของ Infographic<span className="required">*</span>
                                    </p>

                                    <div className="summarize-tone-wrapper">
                                        <Select
                                            options={toneOptions}
                                            value={selectTone}
                                            onChange={handleToneChange}
                                            placeholder="เลือก Tone"
                                            classNamePrefix="select"
                                            styles={selectSummarizeColourStyles}
                                        />
                                    </div>

                                    <p className="summarize-form-title">
                                        2. เลือกผู้พูดที่ต้องการไฮไลต์เนื้อหา (ไม่บังคับ)
                                    </p>

                                    <div className="summarize-checkbox-wrapper">
                                        {
                                            speakerList && speakerList.map(item => (
                                                <div key={item} className="summarize-checkbox">
                                                    <FormControlLabel
                                                        control={<Checkbox
                                                            name={item}
                                                            checked={selectedSpeakers.includes(item)}
                                                            onChange={(event, checked) => handleSpeakerChange(event, checked)}
                                                        />}
                                                        label={`เนื้อหา ${item.replace('Speaker', 'ผู้พูด')}`}
                                                    />
                                                </div>
                                            ))
                                        }
                                    </div>

                                    <Button
                                        variant="contained"
                                        className="summarize-submit-button"
                                        onClick={onSubmit}
                                        disabled={isDisabled}
                                    >
                                        สรุปเนื้อหาอีกครั้ง
                                    </Button>
                                </div>
                    }
                </Modal.Body>
            </Modal>

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                duration={snackbar.duration}
                onClose={hideSnackbar}
            />
        </>
    )
}

export default InfographicSummarizeFormModal;